import { useUpdatePreferences, useUserPreferencesValue } from '@/features/auth/preferences';
import { type LibraryMedia, getMediaUrlById, listLibrary } from '@/features/media-library/api';
import { Button } from '@/shared/components/ui/Button';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { lerRascunho, salvarRascunho } from '../rascunhos';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { renderContactVars } from '@/shared/lib/contact-vars';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  Check,
  FilePen,
  FileText,
  Image as ImageIcon,
  Loader2,
  Mic,
  Music,
  Paperclip,
  Pencil,
  Plus,
  Send,
  Smile,
  Trash2,
  UserCheck,
  UserX,
  Video,
  X,
  Zap,
} from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createMyQuickReply, deleteMyQuickReply, updateMyQuickReply, uploadMedia } from '../api';
import { useQuickReplies } from '../hooks';
import { compressImage } from '../lib/compress-image';
import { extractVideoPreview } from '../lib/extract-video-dimensions';
import { filtrarRapidas, grauDaRapida, termoDaBusca } from '../lib/mensagens-rapidas';
import type { ChatMessage, QuickReply } from '../types';
import { AudioPreview } from './AudioPreview';
import { AudioRecorder } from './AudioRecorder';
import { EmojiPicker } from './EmojiPicker';
import { applySignature, separarAssinatura } from '../lib/assinatura';
import { MAX_ANEXOS, ondeVaiALegenda, quantosCabem } from '../lib/anexos';

type MediaType = 'image' | 'audio' | 'video' | 'document';

export interface SendMediaInput {
  type: MediaType;
  media_id: number;
  caption?: string;
  filename?: string;
  /** Dimensões da imagem — propagadas pro optimistic ChatMessage pra
   *  bolha nascer com aspect-ratio reservado igual inbound. */
  width?: number;
  height?: number;
  /** Poster dataURL (vídeo) capturado client-side da 1ª frame. Vira
   *  media_thumbnail_url na bolha optimistic → zero flash de "preto → thumb".
   *  A URL HMAC real (ffmpeg server-side) chega depois e substitui. */
  poster?: string | null;
}

interface Props {
  disabled?: boolean;
  /** Texto do campo quando `disabled` por outro motivo que não pendente/fechado
   *  (ex.: atendimento de colega aberto só pra visualizar). */
  disabledMessage?: string;
  /** Janela de 24h expirada (Cloud API): composer fica travado como sempre,
   *  MAS o botão de nota interna segue habilitado — clicar nele destrava o
   *  campo no modo nota (âmbar). A nota não vai pro WhatsApp, então a regra
   *  da Meta não se aplica. Anexo/áudio/envio real permanecem bloqueados. */
  noteOnly?: boolean;
  isPending?: boolean;
  contactDisplayName?: string | null;
  /** Demais campos do contato pra tokens canônicos nas respostas rápidas
   *  ({{phone}}, {{email}}, {{state_uf}}, {{custom.X}}). */
  contactPhone?: string | null;
  contactEmail?: string | null;
  contactStateUf?: string | null;
  contactCustomFields?: Record<string, unknown> | null;
  /** Mensagem sendo respondida (citação) — banner visível acima do input. */
  replyingTo?: ChatMessage | null;
  /** X no banner de citação cancela o reply. */
  onCancelReply?: () => void;
  /** Mensagem sendo EDITADA — faixa "Editando mensagem"; o campo recebe o
   *  texto dela (sem a assinatura) e Enter salva via `onSaveEdit`. */
  editing?: ChatMessage | null;
  /** X na faixa ou Esc cancela a edição. */
  onCancelEdit?: () => void;
  /** Recebe o texto FINAL — com ou sem assinatura conforme o botão de
   *  assinatura AGORA (não como a msg foi enviada). */
  onSaveEdit?: (text: string) => void;
  onSend: (text: string) => Promise<void> | void;
  /**
   * Avisa quando a caixa de texto passa de vazia pra com texto e vice-versa
   * (só nas TRANSIÇÕES, não a cada tecla). O agrupador no modo inteligente
   * usa isso pra parar/retomar o relógio do pacote com uma chamada só.
   */
  onTextPresenceChange?: (hasText: boolean) => void;
  /**
   * Conversa dona do rascunho (id do ticket). Com isso o texto digitado
   * sobrevive à troca de conversa — igual ao WhatsApp. Só em memória: F5
   * apaga (decisão do dono). Sem, o compositor nasce vazio e não guarda nada.
   */
  draftKey?: number | null;
  onSendMedia?: (input: SendMediaInput) => Promise<void> | void;
  /** Envia uma nota interna (fica só no painel, não vai pro cliente). Quando
   *  ausente, o botão de nota some. */
  onSendNote?: (text: string) => Promise<void> | void;
  /** Abre o modal "Agendar mensagem". Ausente = sem o botão (canal sem
   *  agendamento: hoje só QR Code tem). */
  onSchedule?: () => void;
  /** Lista de atendimentos escondida (janela estreita): os ícones viram um
   *  menu nos três pontinhos, pro campo não ficar esmagado. */
  menuCompacto?: boolean;
  /** Canal que não aceita documento (Instagram): anexo só de imagem, áudio
   *  e vídeo. */
  semDocumento?: boolean;
}

/** Handle exposto via ref pra ThreadPanel injetar arquivos arrastados/soltos
 *  E pra focar o textarea quando troca de ticket (paridade com WhatsApp Web). */
export interface ComposerHandle {
  uploadFiles: (files: File[]) => Promise<void>;
  focusInput: () => void;
  /** Põe de volta no campo o texto de um pacote descartado (agrupador). */
  devolverTexto: (texto: string) => void;
}

interface PendingAttachment {
  /** Identidade na lista — o mesmo arquivo pode ser anexado duas vezes. */
  key: string;
  /** carregando = comprimindo/subindo; pronto = já tem media_id; erro = não subiu. */
  status: 'carregando' | 'pronto' | 'erro';
  erro?: string;
  /** Áudio gravado no microfone: ocupa o lugar do campo com o player (sozinho). */
  gravado?: boolean;
  type: MediaType;
  /** 0 enquanto carrega. */
  mediaId: number;
  filename: string;
  size: number;
  previewUrl: string;
  /** Para áudio gravado: duração estimada em segundos (vinda do timer da gravação). */
  durationSec?: number;
  /** Dimensões da imagem (vinda do compressImage). Permite bolha optimistic
   *  nascer dimensionada via aspect-ratio igual inbound. */
  width?: number;
  height?: number;
  /** Poster dataURL da 1ª frame (vídeo) capturado client-side. */
  poster?: string | null;
}

const MIME_TO_TYPE = (mime: string): MediaType => {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  return 'document';
};

// Limites Meta WhatsApp Cloud API (https://developers.facebook.com/docs/whatsapp/cloud-api/reference/messages):
// imagem 5MB, áudio 16MB, vídeo 16MB, documento 100MB.
const MAX_BYTES_BY_TYPE: Record<MediaType, number> = {
  image: 5 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

// Ícone + rótulo por tipo, usado na seção "Biblioteca" do dropdown do "/".
const LIB_TYPE_META: Record<MediaType, { Icon: typeof ImageIcon; label: string }> = {
  image: { Icon: ImageIcon, label: 'Imagem' },
  video: { Icon: Video, label: 'Vídeo' },
  audio: { Icon: Music, label: 'Áudio' },
  document: { Icon: FileText, label: 'Documento' },
};

export const Composer = forwardRef<ComposerHandle, Props>(function Composer(
  {
    disabled,
    disabledMessage,
    noteOnly,
    isPending,
    contactDisplayName,
    contactPhone,
    contactEmail,
    contactStateUf,
    contactCustomFields,
    replyingTo,
    onCancelReply,
    editing,
    onCancelEdit,
    onSaveEdit,
    onSend,
    onTextPresenceChange,
    draftKey,
    onSendMedia,
    onSendNote,
    onSchedule,
    menuCompacto = false,
    semDocumento = false,
  },
  ref,
) {
  // Nasce com o rascunho da conversa (se houver) — o que o atendente digitou
  // e não enviou volta exatamente como estava.
  const [value, setValue] = useState(() => (draftKey != null ? lerRascunho(draftKey) : ''));
  // Guarda o rascunho ao desmontar (trocar de conversa) — é só memória, uma
  // escrita por troca basta.
  const valueRef = useRef(value);
  valueRef.current = value;
  // Edição: o campo passa a mostrar o texto da msg editada; o que o atendente
  // estava digitando fica guardado aqui e volta ao sair da edição (e é ELE
  // que vira rascunho — nunca o texto em edição).
  const editandoId = editing?.id ?? null;
  // Em edição: anexar, agendar, nota interna e gravar áudio ficam travados —
  // editar é só trocar o texto, nada pode virar mensagem nova.
  const emEdicao = editandoId !== null;
  const editandoIdRef = useRef<number | null>(null);
  const rascunhoAntesDaEdicaoRef = useRef('');
  useEffect(() => {
    if (draftKey == null) return;
    return () =>
      salvarRascunho(
        draftKey,
        editandoIdRef.current !== null ? rascunhoAntesDaEdicaoRef.current : valueRef.current,
      );
  }, [draftKey]);
  useEffect(() => {
    const anterior = editandoIdRef.current;
    editandoIdRef.current = editandoId;
    if (editandoId === anterior) return;
    if (editandoId !== null && editing) {
      if (anterior === null) rascunhoAntesDaEdicaoRef.current = valueRef.current;
      // O campo mostra só o texto: a assinatura sai daqui e, ao salvar, entra
      // (ou não) conforme o botão de assinatura.
      setValue(separarAssinatura(editing.body ?? '').texto);
      setNoteMode(false);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      });
    } else if (editandoId === null && anterior !== null) {
      setValue(rascunhoAntesDaEdicaoRef.current);
      rascunhoAntesDaEdicaoRef.current = '';
    }
  }, [editandoId, editing]);
  // Transição vazio↔texto: dispara UMA vez em cada sentido, inclusive no
  // primeiro render (a conversa pode abrir já com rascunho — o pacote aberto
  // precisa saber). Nota interna não conta (não vai pro cliente). Texto em
  // EDIÇÃO também não: não é mensagem nova, não segura o pacote.
  const temTexto = editandoId === null && value.trim().length > 0;
  // Esvaziou (enviou, ou apagou tudo): o rascunho sai da lista NA HORA. Sem
  // isso o card ficava em "Rascunho: …" até a troca de conversa, porque a
  // gravação de cima só acontece ao desmontar. Sem rascunho guardado é no-op.
  useEffect(() => {
    if (draftKey != null && !temTexto && editandoId === null) salvarRascunho(draftKey, '');
  }, [draftKey, temTexto, editandoId]);
  const temTextoRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (temTexto === temTextoRef.current) return;
    temTextoRef.current = temTexto;
    onTextPresenceChange?.(temTexto);
  }, [temTexto, onTextPresenceChange]);
  // Modo nota interna: campo fica amarelo; o que enviar vira nota (só no painel).
  const [noteMode, setNoteMode] = useState(false);

  // Janela 24h expirada SEM modo nota ativo: composer fica travado como antes
  // (só o botão de nota habilitado — clicar nele destrava pro modo nota).
  const windowLocked = Boolean(noteOnly) && !noteMode;
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickQuery, setQuickQuery] = useState('');
  // Destaque escolhido no teclado/mouse; null = melhor casamento da busca.
  const [quickIndexEscolhido, setQuickIndexEscolhido] = useState<number | null>(null);
  const quickListRef = useRef<HTMLDivElement>(null);
  // O Chrome manda mousemove "falso" quando a lista rola sob o cursor parado;
  // sem conferir a posição, o destaque do teclado pularia pro item do mouse.
  const ultimoPonteiroRef = useRef<{ x: number; y: number } | null>(null);
  // Vários anexos por envio (até MAX_ANEXOS). Cada um sobe sozinho, na fila
  // (um por vez), e a lista é enviada na ordem em que foi montada.
  const [anexos, setAnexos] = useState<PendingAttachment[]>([]);
  const anexosRef = useRef(anexos);
  anexosRef.current = anexos;
  const chaveDoAnexoRef = useRef(0);
  const filaDeUploadRef = useRef<Promise<void>>(Promise.resolve());
  const audioGravado = anexos.find((a) => a.gravado) ?? null;
  const temAnexo = anexos.length > 0;
  const carregandoAnexo = anexos.some((a) => a.status === 'carregando');
  const anexoComErro = anexos.some((a) => a.status === 'erro');
  // Clipe: nota e edição são só texto; lista cheia e áudio gravado esperando
  // envio também travam (o áudio gravado vai sozinho).
  const anexarTravado =
    Boolean(disabled) ||
    Boolean(noteOnly) ||
    noteMode ||
    emEdicao ||
    anexos.length >= MAX_ANEXOS ||
    audioGravado !== null;
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [menuAcoesAberto, setMenuAcoesAberto] = useState(false);
  // Transição stop→prévia: enquanto o áudio gravado faz upload, o anexo ainda
  // não existe. Sem isso, o composer vazio (textarea+mic) pisca no intervalo.
  // Enquanto true, mostra "Preparando áudio…" no lugar.
  const [finalizingAudio, setFinalizingAudio] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  // Modo estreito: o emoji mora no menu do "+" e o seletor abre preso a ele.
  const botaoMaisRef = useRef<HTMLButtonElement>(null);

  const quickRepliesQ = useQuickReplies();

  // Assinatura: prefs do user (sincronizadas entre devices via DB) + nome do user logado.
  // Quando ativo, prefixa toda mensagem outbound de texto/caption com `*Nome:*\n`.
  const userName = useAuthStore((s) => s.user?.name);
  // Leitor, não pedinte: o Composer remonta a cada troca de conversa, e
  // observador novo com dado vencido rebuscava. AppShell/Topbar ficam
  // montados a sessão toda e seguram a frescura.
  const prefsQuery = useUserPreferencesValue();
  const updatePrefs = useUpdatePreferences();
  const signatureEnabled = prefsQuery.data?.signatureEnabled ?? false;
  // Gestor pode travar (allow_disable_signature=false): atendente não desliga.
  // Default true (não travado). Quando travado → sempre assina.
  const allowDisableSignature = prefsQuery.data?.allowDisableSignature ?? true;
  const signatureLocked = !allowDisableSignature;
  // Valor EFETIVO aplicado nas mensagens: travado → sempre on; senão preferência.
  const signatureActive = signatureLocked || signatureEnabled;
  const toggleSignature = (): void => {
    if (signatureLocked) return; // travado pelo gestor — no-op
    updatePrefs.mutate({ signatureEnabled: !signatureEnabled });
  };

  useEffect(() => {
    const tokens = value.split(/\s+/);
    const last = tokens[tokens.length - 1] ?? '';
    if (last.startsWith('/')) {
      setQuickQuery(last);
      setQuickOpen(true);
      // Busca nova: o destaque volta pro melhor casamento.
      setQuickIndexEscolhido(null);
    } else {
      setQuickOpen(false);
    }
  }, [value]);

  // Abrir a lista com `/` busca as mensagens rápidas do servidor. É o único
  // gatilho — elas não vencem por tempo (vide CADASTROS_STALE_MS). Assim o
  // atendente vê na hora o que o admin acabou de criar ou apagar.
  useRefetchOnOpen(quickOpen, quickRepliesQ.refetch);

  // Auto-resize: textarea cresce com o conteúdo (até maxHeight definida no
  // style). Reset pra 'auto' antes de ler scrollHeight pra que o navegador
  // recalcule o tamanho mínimo necessário — sem isso, o textarea só cresce
  // e nunca volta a encolher (ex: ao apagar tudo).
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxH = 160;
    ta.style.height = `${Math.min(ta.scrollHeight, maxH)}px`;
  }, [value]);

  // Mostra TODAS as quick replies que batem com o filtro. O dropdown tem
  // `max-h-[280px] overflow-y-auto` (vide JSX), então passa scroll se a lista
  // crescer — sem cap silencioso. Antes era `.slice(0, 6)` e escondia o resto
  // sem aviso quando o tenant tinha >6 cadastradas.
  // A palavra vale em qualquer parte do atalho e do texto, sem acento — vide
  // lib/mensagens-rapidas (antes só casava o começo do atalho).
  const filteredQuick = filtrarRapidas(quickRepliesQ.data ?? [], quickQuery);
  // Duas seções no dropdown: as MINHAS (pessoais, editáveis) primeiro, as da
  // EMPRESA depois. O backend já ordena minhas→empresa; aqui só separo pra
  // renderizar os cabeçalhos e os botões de editar/excluir só nas minhas.
  const minhasQuick = filteredQuick.filter((q) => q.mine);
  const empresaQuick = filteredQuick.filter((q) => !q.mine);

  // Biblioteca de mídia no "/": com a barra sozinha mostra as mídias recentes;
  // digitando um termo após o "/" filtra por nome (ex.: "/logo" → "logo").
  const libTerm = quickQuery.startsWith('/') ? quickQuery.slice(1).trim() : '';
  const libraryQ = useQuery({
    queryKey: ['composer-library', libTerm],
    queryFn: () => listLibrary({ q: libTerm, limit: 12 }),
    enabled: quickOpen,
    staleTime: 30_000,
  });
  const libraryItems = quickOpen ? (libraryQ.data?.items ?? []) : [];

  // Teclado no "/": ↑/↓ movem o destaque, Enter escolhe, Esc fecha. A lista
  // plana segue a ordem da tela (minhas → empresa → mídias). Sem escolha
  // (`null`), o destaque fica no melhor casamento — atalho que começa com a
  // palavra ganha de quem só a tem no texto, mesmo que este venha antes.
  const opcoesDoRapido = [
    ...minhasQuick.map((q) => ({ tipo: 'rapida' as const, q })),
    ...empresaQuick.map((q) => ({ tipo: 'rapida' as const, q })),
    ...libraryItems.map((item) => ({ tipo: 'midia' as const, item })),
  ];
  const termoRapido = termoDaBusca(quickQuery);
  let melhorIndiceRapido = 0;
  let melhorGrauRapido = Number.POSITIVE_INFINITY;
  opcoesDoRapido.forEach((o, i) => {
    if (o.tipo !== 'rapida') return;
    const grau = grauDaRapida(o.q, termoRapido) ?? Number.POSITIVE_INFINITY;
    if (grau < melhorGrauRapido) {
      melhorGrauRapido = grau;
      melhorIndiceRapido = i;
    }
  });
  const quickIndex =
    opcoesDoRapido.length === 0
      ? -1
      : Math.min(quickIndexEscolhido ?? melhorIndiceRapido, opcoesDoRapido.length - 1);

  // Resolve variáveis canônicas (renderer compartilhado, espelho do backend):
  // {{firstname}}, {{name}}, {{phone}}, {{email}}, {{state_uf}}, {{custom.X}} =
  // dados do CONTATO; {{atendente}}/{{agente}}/{{user.name}} = ATENDENTE logado.
  // Aplicado na resposta rápida E no envio (cobre texto digitado direto).
  const renderVars = (text: string): string =>
    renderContactVars(text, {
      contact: {
        displayName: contactDisplayName,
        phone: contactPhone,
        email: contactEmail,
        stateUf: contactStateUf,
        customFields: contactCustomFields,
      },
      userName,
    });

  const expand = (q: QuickReply): string => renderVars(q.body);

  // Seleciona uma mídia da biblioteca pelo "/" — entra na lista de anexos (staged)
  // pro atendente revisar/legendar e enviar. Reusa o media_id existente (sem
  // re-upload) — o /messages aceita media_id do tenant.
  const handleLibraryPick = async (item: LibraryMedia): Promise<void> => {
    setQuickOpen(false);
    // Remove o token "/..." do input (não insere texto, só anexa a mídia).
    setValue((prev) => {
      const tokens = prev.split(/\s+/);
      tokens[tokens.length - 1] = '';
      return tokens.join(' ').replace(/\s+$/, '');
    });
    setUploadError(null);
    if (audioGravado) {
      toast.error('Envie ou descarte o áudio gravado antes de anexar.');
      return;
    }
    if (quantosCabem(anexosRef.current.length, 1).aceitos === 0) {
      toast.error(`Até ${MAX_ANEXOS} arquivos por envio.`);
      return;
    }
    try {
      const { url } = await getMediaUrlById(item.id);
      setAnexos((prev) => [
        ...prev,
        {
          key: `a${++chaveDoAnexoRef.current}`,
          status: 'pronto',
          type: item.type,
          mediaId: item.id,
          filename: item.original_name ?? item.label,
          size: item.size_bytes ?? 0,
          previewUrl: url,
          ...(item.duration_ms ? { durationSec: item.duration_ms / 1000 } : {}),
        },
      ]);
    } catch {
      setUploadError('Não foi possível carregar a mídia da biblioteca.');
    }
    inputRef.current?.focus();
  };

  const insertEmoji = (emoji: string): void => {
    const ta = inputRef.current;
    if (!ta) {
      setValue((v) => v + emoji);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    setValue(next);
    // Reposiciona cursor logo após o emoji inserido. RAF garante que o React
    // já reconciliou o novo `value` no textarea antes de aplicar selectionRange.
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = start + emoji.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const handleQuickPick = (q: QuickReply): void => {
    const expanded = expand(q);
    setValue((prev) => {
      const tokens = prev.split(/\s+/);
      tokens[tokens.length - 1] = expanded;
      return tokens.join(' ');
    });
    setQuickOpen(false);
    inputRef.current?.focus();
  };

  const escolherDoRapido = (i: number): void => {
    const opcao = opcoesDoRapido[i];
    if (!opcao) return;
    if (opcao.tipo === 'rapida') handleQuickPick(opcao.q);
    else void handleLibraryPick(opcao.item);
  };

  // Mouse e teclado dividem o mesmo destaque (um só item marcado por vez).
  const destacarPeloMouse = (e: React.MouseEvent, i: number): void => {
    const ultimo = ultimoPonteiroRef.current;
    if (ultimo && ultimo.x === e.clientX && ultimo.y === e.clientY) return;
    ultimoPonteiroRef.current = { x: e.clientX, y: e.clientY };
    setQuickIndexEscolhido(i);
  };

  // ── Mensagens rápidas PESSOAIS: mini-form de criar/editar + excluir ──────
  // `quickForm`: null = fechado; 'new' = criando; QuickReply = editando aquela.
  const [quickForm, setQuickForm] = useState<QuickReply | 'new' | null>(null);
  const qc = useQueryClient();
  const invalidateQuick = (): void => {
    void qc.invalidateQueries({ queryKey: ['quick-replies'] });
  };
  const onQuickError = (err: unknown): void => {
    if (err instanceof ApiError)
      toast.error(err.problem.title, { description: err.problem.detail });
    else toast.error('Não consegui salvar a mensagem rápida');
  };
  const createQuickMut = useMutation({
    mutationFn: createMyQuickReply,
    onSuccess: () => {
      invalidateQuick();
      setQuickForm(null);
      toast.success('Mensagem rápida criada');
    },
    onError: onQuickError,
  });
  const updateQuickMut = useMutation({
    mutationFn: (v: { id: number; shortcut: string; body: string }) =>
      updateMyQuickReply(v.id, { shortcut: v.shortcut, body: v.body }),
    onSuccess: () => {
      invalidateQuick();
      setQuickForm(null);
      toast.success('Mensagem rápida atualizada');
    },
    onError: onQuickError,
  });
  const deleteQuickMut = useMutation({
    mutationFn: deleteMyQuickReply,
    onSuccess: invalidateQuick,
    onError: onQuickError,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // permite selecionar o mesmo arquivo de novo
    adicionarArquivos(files);
  };

  /**
   * Colar (Ctrl+V de print, ou arquivos copiados no Finder/Explorer) — pega
   * TODOS os itens do tipo arquivo e anexa. Texto puro segue o comportamento
   * normal do campo (não chama preventDefault).
   */
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    if (disabled || noteOnly) return;
    // Modo nota e edição são só texto — paste de arquivo não vira anexo (texto
    // cola normal).
    if (noteMode || emEdicao) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = Array.from(items)
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return;
    e.preventDefault();
    adicionarArquivos(files);
  };

  useImperativeHandle(ref, () => ({
    // Arrastar e soltar (e colar fora do campo) chega aqui pelo ThreadPanel —
    // com todos os arquivos, não só o primeiro.
    uploadFiles: async (files: File[]) => {
      if (!files.length || disabled || noteOnly || noteMode || emEdicao) return;
      adicionarArquivos(files);
    },
    // Foca o textarea — chamado pelo ThreadPanel quando troca de ticket
    // (paridade com WhatsApp Web: clicou no chat, cursor já no campo de
    // digitar). RAF garante que o React reconciliou e o textarea já existe
    // no DOM (em troca rápida de ticket pode ainda estar montando).
    focusInput: () => {
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        // Cursor no FIM do que já está escrito (rascunho), não no começo.
        el.setSelectionRange(el.value.length, el.value.length);
      });
    },
    // Descartou o pacote que ia sair: o texto volta pro campo, pro atendente
    // corrigir e mandar de novo (ou apagar). Sem a assinatura `*Nome:*` — ela
    // é reaplicada no envio conforme o botão de assinatura. Se ele já começou
    // a digitar outra coisa, o texto devolvido entra ANTES, sem apagar nada.
    devolverTexto: (texto: string) => {
      const devolvido = separarAssinatura(texto).texto.trim();
      if (!devolvido) return;
      setValue((atual) => (atual.trim() ? `${devolvido}\n${atual}` : devolvido));
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      });
    },
  }));

  // Trocar de conversa desmonta o compositor: libera as prévias locais.
  useEffect(
    () => () => {
      for (const a of anexosRef.current)
        if (a.previewUrl.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl);
    },
    [],
  );

  /**
   * Prepara UM arquivo e sobe: comprime imagem, tira dimensões/capa de vídeo,
   * confere o limite da Meta e faz o upload. Devolve o que o anexo precisa;
   * falha vira exceção com a mensagem pro atendente.
   */
  const prepararArquivo = async (
    file: File | Blob,
    filename?: string,
    extra?: { durationSec?: number; waveform?: number[] },
  ): Promise<Omit<PendingAttachment, 'key' | 'status' | 'previewUrl'> & { blob: Blob }> => {
    const mime = file.type || 'application/octet-stream';
    const type = MIME_TO_TYPE(mime);

    // Imagem: força JPEG sempre (PNG/JPG/WEBP → JPEG q=0.85, max 1920px).
    // Reduz drasticamente PNGs grandes (screenshot 2MB → ~280KB) e padroniza
    // formato — fundo branco preserva visual de PNG transparente.
    let toUpload: File | Blob = file;
    let toUploadName = filename ?? (file instanceof File ? file.name : 'upload');
    let mediaWidth: number | null = null;
    let mediaHeight: number | null = null;
    let videoPoster: string | null = null;
    if (type === 'image') {
      try {
        const compressed = await compressImage(file, { forceJpeg: true });
        if (compressed.blob !== file) {
          toUpload = compressed.blob;
          if (compressed.blob.type === 'image/jpeg' && !/\.jpe?g$/i.test(toUploadName)) {
            toUploadName = toUploadName.replace(/\.[^.]+$/, '') + '.jpg';
          }
        }
        mediaWidth = compressed.width;
        mediaHeight = compressed.height;
      } catch (err) {
        // Falha na compressão não bloqueia — tenta upload do original.
        console.warn('image compression failed; uploading original', err);
      }
    } else if (type === 'video') {
      // Extrai dimensões + 1ª frame (poster) do vídeo via <video> temp.
      // Dimensões → API persiste em media_files.width/height (reserva
      // aspect-ratio na bolha). Poster → media_thumbnail_url na bolha
      // optimistic, eliminando o flash de "preto → thumb" (a URL HMAC real
      // do ffmpeg server-side chega depois e substitui transparente).
      try {
        const preview = await extractVideoPreview(file);
        mediaWidth = preview.width;
        mediaHeight = preview.height;
        videoPoster = preview.poster;
      } catch (err) {
        console.warn('video preview extraction failed; uploading without', err);
      }
    }

    const max = MAX_BYTES_BY_TYPE[type];
    if (toUpload.size > max) {
      throw new Error(`Passa do limite de ${(max / 1024 / 1024).toFixed(0)} MB`);
    }

    const result = await uploadMedia(toUpload, toUploadName, {
      ...(extra?.durationSec !== undefined && { durationSec: extra.durationSec }),
      ...(extra?.waveform && extra.waveform.length > 0 && { waveform: extra.waveform }),
      ...(mediaWidth && mediaHeight && { width: mediaWidth, height: mediaHeight }),
    });
    return {
      blob: toUpload,
      type,
      mediaId: result.media_id,
      filename: result.filename ?? toUploadName,
      size: result.size_bytes,
      ...(extra?.durationSec !== undefined && { durationSec: extra.durationSec }),
      ...(mediaWidth && mediaHeight && { width: mediaWidth, height: mediaHeight }),
      ...(videoPoster && { poster: videoPoster }),
    };
  };

  /**
   * Anexa arquivos (clipe, colar, arrastar). Entram na lista na hora — com
   * miniatura e "carregando" — e sobem um por vez, na ordem. O que passa do
   * teto ou não serve pro canal fica de fora com aviso, nunca em silêncio.
   */
  const adicionarArquivos = (files: File[]): void => {
    if (files.length === 0) return;
    setUploadError(null);
    if (audioGravado) {
      toast.error('Envie ou descarte o áudio gravado antes de anexar.');
      return;
    }
    let lista = files;
    // Instagram não aceita documento.
    if (semDocumento) {
      const semDocs = lista.filter(
        (f) => MIME_TO_TYPE(f.type || 'application/octet-stream') !== 'document',
      );
      if (semDocs.length < lista.length) {
        toast.error('O Instagram não aceita documento', {
          description: 'Por este canal dá pra enviar texto, imagem, áudio e vídeo.',
        });
      }
      lista = semDocs;
    }
    const { aceitos, recusados } = quantosCabem(anexosRef.current.length, lista.length);
    if (recusados > 0) {
      toast.error(`Até ${MAX_ANEXOS} arquivos por envio`, {
        description:
          recusados === 1 ? '1 arquivo ficou de fora.' : `${recusados} arquivos ficaram de fora.`,
      });
    }
    const novos = lista.slice(0, aceitos).map((file) => {
      const type = MIME_TO_TYPE(file.type || 'application/octet-stream');
      return {
        file,
        anexo: {
          key: `a${++chaveDoAnexoRef.current}`,
          status: 'carregando' as const,
          type,
          mediaId: 0,
          filename: file.name,
          size: file.size,
          // Miniatura na hora, do arquivo original (a comprimida sobe depois).
          previewUrl: type === 'image' ? URL.createObjectURL(file) : '',
        },
      };
    });
    if (novos.length === 0) return;
    setAnexos((prev) => [...prev, ...novos.map((n) => n.anexo)]);
    for (const { file, anexo } of novos) {
      filaDeUploadRef.current = filaDeUploadRef.current.then(() => subirAnexo(anexo.key, file));
    }
    // Foca o campo pra digitar a legenda sem precisar clicar.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const subirAnexo = async (key: string, file: File): Promise<void> => {
    // Removido antes de chegar a vez dele: nem sobe.
    if (!anexosRef.current.some((a) => a.key === key)) return;
    try {
      const { blob: _blob, ...pronto } = await prepararArquivo(file);
      setAnexos((prev) =>
        prev.map((a) => (a.key === key ? { ...a, ...pronto, status: 'pronto' as const } : a)),
      );
    } catch (err) {
      const erro = err instanceof Error ? err.message : 'Falha no upload';
      setAnexos((prev) =>
        prev.map((a) => (a.key === key ? { ...a, status: 'erro' as const, erro } : a)),
      );
    }
  };

  const removerAnexo = (key: string): void => {
    const alvo = anexosRef.current.find((a) => a.key === key);
    if (alvo?.previewUrl.startsWith('blob:')) URL.revokeObjectURL(alvo.previewUrl);
    setAnexos((prev) => prev.filter((a) => a.key !== key));
  };

  const submit = (): void => {
    if (disabled) return;
    // Edição: salva o texto novo da msg em vez de enviar uma nova. Assinatura
    // segue o botão AGORA: desligado → vai sem, mesmo que a original tivesse;
    // ligado → vai com (nome de quem edita). Anexo pendente fica de fora.
    if (editandoId !== null) {
      const texto = renderVars(value.trim());
      if (!texto || !onSaveEdit) return;
      onSaveEdit(applySignature(texto, userName, signatureActive));
      return;
    }
    // Janela expirada: só nota pode sair daqui — sem modo nota ativo, o
    // composer está travado e nada é enviado.
    if (noteOnly && !noteMode) return;

    // Nota interna: texto puro (sem assinatura, sem mídia). Fica só no painel.
    if (noteMode) {
      const text = value.trim();
      if (!text || !onSendNote) return;
      setValue('');
      Promise.resolve(onSendNote(text)).catch(() => {
        /* erro tratado no onError da mutation (toast + bolha vermelha) */
      });
      return;
    }

    if (temAnexo) {
      // Só sai com TODOS prontos: carregando espera; com erro, o atendente
      // remove o que falhou (o botão de enviar explica).
      if (!onSendMedia || carregandoAnexo || anexoComErro) return;
      // UX OTIMISTA (re-introduzida em 2026-05-16 — vide CLAUDE.md):
      // Limpa composer IMEDIATO. Mutation roda em background. Em erro,
      // a bolha optimistic vira vermelha (handleSendError em ThreadPanel)
      // + toast aparece — user pode copiar o texto da bolha pra retry.
      // Composer fica sempre livre, atendente pode mandar rajada de msgs
      // sem esperar.
      const legenda = applySignature(renderVars(value.trim()), userName, signatureActive);
      // Legenda só no último arquivo (decisão do dono) — vide ondeVaiALegenda.
      const { indice, textoSeparado } = ondeVaiALegenda(
        anexos.map((a) => a.type),
        legenda,
      );
      const envios = anexos.map((a, i) => ({
        type: a.type,
        media_id: a.mediaId,
        ...(i === indice && { caption: legenda }),
        filename: a.filename,
        ...(a.width && a.height && { width: a.width, height: a.height }),
        ...(a.poster && { poster: a.poster }),
      }));
      for (const a of anexos)
        if (a.previewUrl.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl);
      setAnexos([]);
      setValue('');
      // Um depois do outro, na ordem da lista: cada POST só sai quando o
      // anterior foi aceito, pra chegarem ao cliente na mesma ordem. Erro num
      // não para os outros — a bolha dele fica vermelha (onError no ThreadPanel).
      void (async () => {
        if (textoSeparado) await Promise.resolve(onSend(textoSeparado)).catch(() => {});
        for (const envio of envios) {
          await Promise.resolve(onSendMedia(envio)).catch(() => {});
        }
      })();
      return;
    }

    if (!value.trim()) return;
    const textWithSig = applySignature(renderVars(value.trim()), userName, signatureActive);
    setValue('');
    Promise.resolve(onSend(textWithSig)).catch(() => {
      /* silent — bolha vermelha + toast cuidam do feedback */
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Ignora Enter durante composição (IME / dead keys). No layout PT-BR,
    // aspas `"` é uma dead key — quando user pressiona Enter ainda em
    // composição, o React dispara o handler 2x (uma com value pré-composição
    // e outra com a `"` que a finalização da composição inseriu), causando
    // duplo envio de mensagem. `nativeEvent.isComposing` + keyCode 229
    // cobre todos os browsers e IMEs.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    // Lista do "/" aberta: as setas andam nela e o Enter escolhe o destacado
    // (sem opção, o Enter segue enviando o texto como antes).
    if (quickOpen && opcoesDoRapido.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = opcoesDoRapido.length;
        const proximo = (quickIndex + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
        setQuickIndexEscolhido(proximo);
        quickListRef.current
          ?.querySelector(`[data-rapida-idx="${proximo}"]`)
          ?.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        escolherDoRapido(quickIndex);
        return;
      }
    }
    if (e.key === 'Escape' && quickOpen) {
      // preventDefault: a busca na conversa também fecha no Esc e respeita isso.
      e.preventDefault();
      e.stopPropagation();
      setQuickOpen(false);
      return;
    }
    if (e.key === 'Escape' && editandoId !== null) {
      e.preventDefault();
      onCancelEdit?.();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  /**
   * Toggle do picker de mensagens rápidas (botão Zap).
   * Insere `/` no final do composer pra disparar o mesmo dropdown que o atalho de teclado abre.
   * Se o picker já está aberto, fecha.
   */
  const toggleQuickPicker = (): void => {
    if (quickOpen) {
      setQuickOpen(false);
      return;
    }
    const trimmed = value.trimEnd();
    setValue(trimmed.length > 0 ? `${trimmed} /` : '/');
    inputRef.current?.focus();
  };

  const showInlineComposer = !recording && !finalizingAudio && !audioGravado;

  // Preview compacto da msg sendo respondida (banner cinza acima do input).
  // Trunca corpo em 1 linha; mídias mostram label genérico ([imagem], [áudio]).
  const replyPreview = (() => {
    if (!replyingTo) return null;
    if (replyingTo.body && replyingTo.body.trim()) return replyingTo.body.trim();
    if (replyingTo.type === 'image') return '[Imagem]';
    if (replyingTo.type === 'video') return '[Vídeo]';
    if (replyingTo.type === 'audio') return '[Áudio]';
    if (replyingTo.type === 'document') return '[Documento]';
    if (replyingTo.type === 'sticker') return '[Sticker]';
    if (replyingTo.type === 'location') return '[Localização]';
    if (replyingTo.type === 'contacts') return '[Contato]';
    return `[${replyingTo.type}]`;
  })();

  return (
    <div className="relative flex flex-col border-n-200 border-t bg-n-0">
      {editing && (
        <div className="flex items-start gap-2 border-n-100 border-b bg-n-50 px-4 py-2">
          <div className="flex-shrink-0 self-stretch w-1 rounded-pill bg-warning-500" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-center gap-1 font-semibold text-[11px] text-warning-600">
              <Pencil size={11} />
              Editando mensagem
            </span>
            <span className="truncate text-[12px] text-n-700">
              {separarAssinatura(editing.body ?? '').texto}
            </span>
          </div>
          <button
            type="button"
            onClick={onCancelEdit}
            aria-label="Cancelar edição"
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {replyingTo && (
        <div className="flex items-start gap-2 border-n-100 border-b bg-n-50 px-4 py-2">
          <div className="flex-shrink-0 self-stretch w-1 rounded-pill bg-brand-500" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="font-semibold text-[11px] text-brand-600">
              {replyingTo.direction === 'outbound' ? 'Você' : 'Resposta a'}
            </span>
            <span className="truncate text-[12px] text-n-700">{replyPreview}</span>
          </div>
          <button
            type="button"
            onClick={onCancelReply}
            aria-label="Cancelar resposta"
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {temAnexo && !audioGravado && (
        <FaixaDeAnexos
          anexos={anexos}
          onRemover={removerAnexo}
          {...(anexos.length < MAX_ANEXOS && { onAdicionar: () => fileInputRef.current?.click() })}
        />
      )}
      {uploadError && (
        <div className="border-danger-100 border-b bg-danger-50 px-4 py-1.5 text-[12px] text-danger-700">
          {uploadError}
        </div>
      )}

      <div
        className={`relative flex flex-shrink-0 items-center gap-[4px] py-3 pr-4 ${
          menuCompacto ? 'pl-4' : 'pl-[4px]'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={
            semDocumento
              ? 'image/*,audio/*,video/*'
              : 'image/*,audio/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv'
          }
          onChange={(e) => void handleFileChange(e)}
        />

        {showInlineComposer && (
          <>
            {/* Ordem (pedido do dono): anexar, agendar, rápidas, assinatura,
                nota — o emoji fica DENTRO do campo, à direita (igual ao
                WhatsApp). Com a lista de atendimentos escondida (janela
                estreita), viram o menu do "+" DENTRO do campo, à esquerda —
                igual ao app do celular — pro campo não ficar esmagado. */}
            {!menuCompacto && (
              <>
                <ComposerIcon
                  icon={carregandoAnexo ? Loader2 : Paperclip}
                  label="Anexar"
                  spinning={carregandoAnexo}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={anexarTravado}
                />
                {onSchedule && (
                  <ComposerIcon
                    icon={CalendarClock}
                    label="Agendar mensagem"
                    onClick={onSchedule}
                    disabled={disabled || noteOnly || noteMode || emEdicao}
                  />
                )}
                <ComposerIcon
                  icon={Zap}
                  label="Mensagens rápidas"
                  onClick={toggleQuickPicker}
                  disabled={disabled || noteOnly || noteMode}
                />
                <ComposerIcon
                  // UserCheck quando ativado (✓ no canto), UserX quando desativado (✗).
                  // Mais legível em relance que cor sozinha — atendente reconhece o estado
                  // sem ler o tooltip. Combina com o `active` que pinta o fundo brand-50.
                  icon={signatureActive ? UserCheck : UserX}
                  label={
                    signatureLocked
                      ? 'Essa função foi desabilitada pelo gestor.'
                      : signatureEnabled
                        ? 'Assinatura ativada'
                        : 'Assinatura desativada'
                  }
                  onClick={toggleSignature}
                  active={signatureActive}
                  // Travado pelo gestor: botão fica visível+inerte (NÃO usa disabled
                  // nativo, senão o tooltip some no hover) com tooltip explicando.
                  locked={signatureLocked}
                  disabled={disabled || noteOnly || updatePrefs.isPending || !userName || noteMode}
                />
                {onSendNote && (
                  <ComposerIcon
                    icon={FilePen}
                    label={
                      windowLocked
                        ? 'Lançar nota interna (liberado mesmo com a janela expirada)'
                        : 'Nota interna'
                    }
                    onClick={() => {
                      const turningOn = !noteMode;
                      setNoteMode(turningOn);
                      // Destravou pro modo nota → cursor direto no campo.
                      if (turningOn) requestAnimationFrame(() => inputRef.current?.focus());
                    }}
                    active={noteMode}
                    activeClassName="bg-warning-50 text-warning-700 hover:text-warning-600"
                    disabled={disabled || temAnexo || recording || finalizingAudio || emEdicao}
                  />
                )}
              </>
            )}

            {/* Tela larga: emoji DENTRO do campo, à direita, ao lado do
                microfone — igual ao WhatsApp. Estreita: vai pro menu do "+". Fica no pé do campo (sobe junto quando o texto
                quebra linha). O seletor abre por cima de tudo, preso ao botão,
                e não sai da tela pela direita. */}
            <div className="relative min-w-0 flex-1">
              {menuCompacto && (
                // "+" do app do celular: dentro do campo, no pé (sobe junto
                // quando o texto quebra linha), abre o menu pra cima.
                <div className="absolute bottom-[5px] left-[5px] z-10">
                  <MenuDeAcoesDoComposer
                    gatilhoRef={botaoMaisRef}
                    aberto={menuAcoesAberto}
                    onAbrirFechar={setMenuAcoesAberto}
                    itens={[
                      {
                        chave: 'anexar',
                        icone: carregandoAnexo ? Loader2 : Paperclip,
                        rotulo: 'Anexar',
                        desabilitado: anexarTravado,
                        onClick: () => fileInputRef.current?.click(),
                      },
                      ...(onSchedule
                        ? [
                            {
                              chave: 'agendar',
                              icone: CalendarClock,
                              rotulo: 'Agendar mensagem',
                              desabilitado: disabled || noteOnly || noteMode || emEdicao,
                              onClick: onSchedule,
                            },
                          ]
                        : []),
                      {
                        chave: 'emoji',
                        icone: Smile,
                        rotulo: 'Emoji',
                        desabilitado: disabled || windowLocked,
                        onClick: () => setEmojiOpen(true),
                      },
                      {
                        chave: 'rapidas',
                        icone: Zap,
                        rotulo: 'Mensagens rápidas',
                        desabilitado: disabled || noteOnly || noteMode,
                        onClick: toggleQuickPicker,
                      },
                      {
                        chave: 'assinatura',
                        icone: signatureActive ? UserCheck : UserX,
                        rotulo: 'Assinatura',
                        ativo: signatureActive,
                        ...(signatureLocked && {
                          dica: 'Essa função foi desabilitada pelo gestor.',
                        }),
                        desabilitado:
                          signatureLocked ||
                          disabled ||
                          noteOnly ||
                          updatePrefs.isPending ||
                          !userName ||
                          noteMode,
                        onClick: toggleSignature,
                      },
                      ...(onSendNote
                        ? [
                            {
                              chave: 'nota',
                              icone: FilePen,
                              rotulo: 'Nota interna',
                              ativo: noteMode,
                              desabilitado:
                                disabled || temAnexo || recording || finalizingAudio || emEdicao,
                              onClick: () => {
                                const turningOn = !noteMode;
                                setNoteMode(turningOn);
                                if (turningOn)
                                  requestAnimationFrame(() => inputRef.current?.focus());
                              },
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              )}
              <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                disabled={disabled || windowLocked}
                rows={1}
                placeholder={
                  disabled
                    ? disabledMessage
                      ? disabledMessage
                      : isPending
                        ? 'Ticket pendente — clique em aceitar para enviar'
                        : 'Ticket fechado — reabra para enviar'
                    : windowLocked
                      ? 'Janela de 24h expirou — envie um template ou lance uma nota interna'
                      : noteMode
                        ? 'Escreva uma nota interna (não vai pro cliente)'
                        : temAnexo
                          ? anexos.length > 1
                            ? 'Legenda do último arquivo (opcional)'
                            : 'Adicione legenda (opcional) e clique enviar'
                          : 'Digite sua mensagem'
                }
                // border-radius adaptativo: pill (full round) quando é 1 linha,
                // rounded-2xl quando expande — pill com várias linhas vira oval.
                // Modo nota: campo âmbar pra deixar claro que não vai pro cliente.
                // Sem nenhum destaque de foco (pedido do dono): nem borda, nem
                // sombra, nem o contorno global de :focus-visible do globals.css.
                className={`block w-full resize-none rounded-2xl border py-2.5 text-[14px] ${
                  menuCompacto ? 'pr-[18px] pl-[46px]' : 'pr-[46px] pl-[18px]'
                } text-n-900 outline-none focus-visible:outline-none disabled:opacity-60 ${
                  noteMode
                    ? 'border-warning-500 bg-warning-50 placeholder:text-warning-600 focus:border-warning-600'
                    : 'border-n-200 bg-n-50 placeholder:text-n-500'
                }`}
                style={{ maxHeight: '160px', minHeight: '42px', overflowY: 'auto' }}
              />
              {!menuCompacto && (
                <button
                  ref={emojiButtonRef}
                  type="button"
                  aria-label="Emoji"
                  aria-pressed={emojiOpen}
                  title="Emoji"
                  onClick={() => setEmojiOpen((v) => !v)}
                  disabled={disabled || windowLocked}
                  className={`absolute right-[6px] bottom-[5px] flex h-[32px] w-[32px] items-center justify-center rounded-pill transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    emojiOpen ? 'text-brand-600' : 'text-n-500 hover:text-n-700'
                  }`}
                >
                  <Smile size={20} strokeWidth={1.75} />
                </button>
              )}
              <EmojiPicker
                open={emojiOpen}
                onClose={() => setEmojiOpen(false)}
                onSelect={insertEmoji}
                anchorRef={menuCompacto ? botaoMaisRef : emojiButtonRef}
                flutuante
              />
            </div>
          </>
        )}

        {recording && (
          <div
            className={`flex min-w-0 flex-shrink-0 items-center gap-1.5 ${
              menuCompacto ? 'flex-1' : 'ml-auto w-1/2'
            }`}
          >
            <AudioRecorder
              onCancel={() => setRecording(false)}
              onComplete={async (blob, durationSec, waveform) => {
                // finalizingAudio (batched com recording) evita o flash do composer
                // vazio entre o stop e a prévia — o anexo só entra após o upload.
                setFinalizingAudio(true);
                setRecording(false);
                try {
                  // Rebadge pra audio/ogg (Chromium produz webm/opus; backend converte
                  // pra ogg/opus puro via ffmpeg antes de enviar pra Meta).
                  const oggBlob = new Blob([blob], { type: 'audio/ogg' });
                  const { blob: subido, ...pronto } = await prepararArquivo(
                    oggBlob,
                    `audio-${Date.now()}.ogg`,
                    { durationSec, waveform },
                  );
                  setAnexos([
                    {
                      ...pronto,
                      key: `a${++chaveDoAnexoRef.current}`,
                      status: 'pronto',
                      gravado: true,
                      previewUrl: URL.createObjectURL(subido),
                    },
                  ]);
                  requestAnimationFrame(() => inputRef.current?.focus());
                } catch (err) {
                  setUploadError(err instanceof Error ? err.message : 'Falha no upload');
                } finally {
                  setFinalizingAudio(false);
                }
              }}
            />
          </div>
        )}

        {!recording && audioGravado && (
          <div
            className={`flex min-w-0 flex-shrink-0 items-center ${
              menuCompacto ? 'flex-1' : 'ml-auto w-1/2'
            }`}
          >
            <AudioPreview
              src={audioGravado.previewUrl}
              estimatedDuration={audioGravado.durationSec ?? 0}
              onDiscard={() => removerAnexo(audioGravado.key)}
            />
          </div>
        )}

        {/* Ponte stop→prévia: enquanto o áudio faz upload, ocupa o espaço no lugar
            do composer vazio (elimina o flash de textarea+mic). Espelha o box da
            AudioPreview (mesmo px-3/py-2 + elemento h-7) pra a altura NÃO pular. */}
        {!recording && finalizingAudio && (
          <div
            className={`flex min-w-0 flex-shrink-0 items-center ${
              menuCompacto ? 'flex-1' : 'ml-auto w-1/2'
            }`}
          >
            <div className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-pill bg-n-100 px-3 py-2">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                <Loader2 size={16} className="animate-spin text-n-500" />
              </span>
              <span className="text-[13px] text-n-500">Preparando áudio…</span>
            </div>
          </div>
        )}

        {/* Botão circular ÚNICO (padrão WhatsApp): microfone quando o composer
            está vazio; vira "enviar" assim que há texto ou mídia pendente. Some
            durante a gravação (o AudioRecorder tem os próprios controles).
            Isso também evita o caso de áudio com legenda (que a Meta rejeita):
            com texto, o botão é "enviar", não microfone. */}
        {!recording &&
          !finalizingAudio &&
          // Modo nota: sempre "enviar" (âmbar), nunca microfone (nota é só texto).
          (noteMode ? (
            <button
              type="button"
              onClick={() => submit()}
              disabled={disabled || !value.trim()}
              aria-label="Enviar nota interna"
              title="Enviar nota interna"
              className="ml-1 flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-pill bg-warning-500 text-white shadow-xs transition hover:bg-warning-600 disabled:opacity-50"
            >
              <Send size={16} strokeWidth={2.2} />
            </button>
          ) : value.trim().length > 0 || temAnexo ? (
            <button
              type="button"
              onClick={() => submit()}
              disabled={
                disabled ||
                windowLocked ||
                (!value.trim() && !temAnexo) ||
                carregandoAnexo ||
                anexoComErro
              }
              aria-label="Enviar mensagem"
              title={
                carregandoAnexo
                  ? 'Aguarde os arquivos terminarem de carregar'
                  : anexoComErro
                    ? 'Remova os arquivos com erro para enviar'
                    : temAnexo && anexos.length > 1
                      ? `Enviar ${anexos.length} arquivos`
                      : undefined
              }
              className="ml-1 flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-pill bg-brand-500 text-white shadow-xs transition hover:bg-brand-600 disabled:opacity-50"
            >
              {carregandoAnexo ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} strokeWidth={2.2} />
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setRecording(true)}
              disabled={disabled || windowLocked || emEdicao}
              aria-label="Gravar áudio"
              className="ml-1 flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-pill bg-brand-500 text-white shadow-xs transition hover:bg-brand-600 disabled:opacity-50"
            >
              <Mic size={18} strokeWidth={1.9} />
            </button>
          ))}

        {quickOpen && (
          <div
            ref={quickListRef}
            className="absolute right-4 bottom-full left-4 mb-2 max-h-[280px] overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-md"
          >
            {/* Criar nova pessoal — FIXO no topo (sticky), sempre visível mesmo
                rolando a lista de mensagens já cadastradas. */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setQuickForm('new');
              }}
              className="sticky top-0 z-10 flex w-full items-center gap-2 border-n-100 border-b bg-n-0 px-4 py-2.5 text-left font-medium text-[13px] text-brand-600 hover:bg-brand-50"
            >
              <Plus size={15} strokeWidth={2} />
              Criar mensagem rápida
            </button>

            {/* MINHAS — pessoais do atendente, com editar/excluir no hover. */}
            {minhasQuick.length > 0 && (
              <>
                <div className="border-n-100 border-b bg-n-50 px-4 py-1.5 font-bold text-[10px] text-n-500 uppercase tracking-wide">
                  Minhas
                </div>
                {minhasQuick.map((q, i) => (
                  <div
                    key={q.id}
                    data-rapida-idx={i}
                    onMouseMove={(e) => destacarPeloMouse(e, i)}
                    className={`group flex scroll-mt-[40px] items-stretch border-n-100 border-b last:border-b-0 ${quickIndex === i ? 'bg-brand-50' : ''}`}
                  >
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleQuickPick(q);
                      }}
                      className="flex min-w-0 flex-1 flex-col gap-1 px-4 py-2.5 text-left"
                    >
                      <span className="font-mono font-semibold text-[12px] text-brand-600">
                        {q.shortcut}
                      </span>
                      <span
                        title={q.body}
                        className="text-[13px] text-n-700"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          lineHeight: 1.4,
                        }}
                      >
                        {q.body}
                      </span>
                    </button>
                    <div className="flex items-center gap-0.5 pr-2 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        title="Editar"
                        aria-label="Editar mensagem rápida"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQuickForm(q);
                        }}
                        className="rounded p-1.5 text-n-400 hover:bg-n-100 hover:text-n-700"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        title="Excluir"
                        aria-label="Excluir mensagem rápida"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          void confirm({
                            title: `Excluir ${q.shortcut}?`,
                            description: 'Essa mensagem rápida é só sua e será removida.',
                            confirmText: 'Excluir',
                            variant: 'danger',
                          }).then((ok) => {
                            if (ok) deleteQuickMut.mutate(q.id);
                          });
                        }}
                        className="rounded p-1.5 text-n-400 hover:bg-danger-50 hover:text-danger-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* DA EMPRESA — só leitura pro atendente. */}
            {empresaQuick.length > 0 && (
              <>
                <div className="border-n-100 border-b bg-n-50 px-4 py-1.5 font-bold text-[10px] text-n-500 uppercase tracking-wide">
                  Da empresa
                </div>
                {empresaQuick.map((q, j) => {
                  const i = minhasQuick.length + j;
                  return (
                    <button
                      key={q.id}
                      type="button"
                      data-rapida-idx={i}
                      onMouseMove={(e) => destacarPeloMouse(e, i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleQuickPick(q);
                      }}
                      className={`flex w-full scroll-mt-[40px] flex-col gap-1 border-n-100 border-b px-4 py-2.5 text-left last:border-b-0 ${quickIndex === i ? 'bg-brand-50' : ''}`}
                    >
                      <span className="font-mono font-semibold text-[12px] text-brand-600">
                        {q.shortcut}
                      </span>
                      <span
                        title={q.body}
                        className="text-[13px] text-n-700"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          lineHeight: 1.4,
                        }}
                      >
                        {q.body}
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {libraryItems.length > 0 && (
              <>
                <div className="border-n-100 border-b bg-n-50 px-4 py-1.5 font-bold text-[10px] text-n-500 uppercase tracking-wide">
                  Biblioteca de mídia
                </div>
                {libraryItems.map((item, k) => {
                  const meta = LIB_TYPE_META[item.type];
                  const Icon = meta.Icon;
                  const i = minhasQuick.length + empresaQuick.length + k;
                  return (
                    <button
                      key={`lib-${item.id}`}
                      type="button"
                      data-rapida-idx={i}
                      onMouseMove={(e) => destacarPeloMouse(e, i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        void handleLibraryPick(item);
                      }}
                      className={`flex w-full scroll-mt-[40px] items-center gap-2.5 border-n-100 border-b px-4 py-2.5 text-left last:border-b-0 ${quickIndex === i ? 'bg-brand-50' : ''}`}
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-n-100 text-n-500">
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-[13px] text-n-800">
                          {item.label}
                        </span>
                        <span className="block text-[11px] text-n-500">{meta.label}</span>
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {quickForm && (
        <QuickReplyFormModal
          mode={quickForm}
          pending={createQuickMut.isPending || updateQuickMut.isPending}
          onClose={() => setQuickForm(null)}
          onSubmit={(shortcut, body) => {
            if (quickForm === 'new') createQuickMut.mutate({ shortcut, body });
            else updateQuickMut.mutate({ id: quickForm.id, shortcut, body });
          }}
        />
      )}
    </div>
  );
});

/**
 * Mini-form da mensagem rápida PESSOAL (criar/editar) — só atalho + texto,
 * sem grupos de acesso. Espelha as regras de validação do backend
 * (`/^\/[\w-]+$/`, corpo 1-4000). Highlight de `{{variável}}` no preview.
 */
const QR_SHORTCUT_REGEX = /^\/[\w-]+$/;

function QuickReplyFormModal({
  mode,
  pending,
  onClose,
  onSubmit,
}: {
  mode: QuickReply | 'new';
  pending: boolean;
  onClose: () => void;
  onSubmit: (shortcut: string, body: string) => void;
}): React.ReactElement {
  const editing = mode !== 'new';
  const [shortcut, setShortcut] = useState(editing ? mode.shortcut : '');
  const [body, setBody] = useState(editing ? mode.body : '');
  // Fecha com ESC — listener no document cobre o foco estar num input filho.
  // Não fecha se estiver salvando (evita descartar no meio do POST/PATCH).
  useEscapeClose(!pending, onClose);
  const shortcutOk = QR_SHORTCUT_REGEX.test(shortcut);
  const canSave = shortcutOk && body.trim().length > 0 && !pending;

  const submit = (): void => {
    if (!canSave) return;
    onSubmit(shortcut.trim(), body.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 shadow-xl">
        <header className="flex items-center justify-between border-n-200 border-b px-5 py-3.5">
          <h2 className="font-bold text-[15px] text-n-900">
            {editing ? 'Editar mensagem rápida' : 'Nova mensagem rápida'}
          </h2>
          <button type="button" onClick={onClose} className="text-n-500 hover:text-n-700">
            <X size={18} />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">Atalho</span>
            <input
              value={shortcut}
              onChange={(e) => {
                // Força começar com "/" e sem espaços — pega o padrão do atalho.
                let v = e.target.value.replace(/\s+/g, '');
                if (v && !v.startsWith('/')) v = `/${v}`;
                setShortcut(v);
              }}
              placeholder="/saudacao"
              // biome-ignore lint/a11y/noAutofocus: modal de foco único, UX de digitar direto
              autoFocus
              className="w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 font-mono text-[13px] text-n-800 outline-none focus:border-brand-500"
            />
            {shortcut.length > 0 && !shortcutOk && (
              <span className="mt-1 block text-[11px] text-danger-600">
                Comece com / e use só letras, números, - ou _
              </span>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block font-semibold text-[12px] text-n-700">Mensagem</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
              }}
              rows={4}
              placeholder="Olá {{firstname}}, tudo bem?"
              className="w-full resize-y rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-800 outline-none focus:border-brand-500"
            />
            <span className="mt-1 block text-[11px] text-n-500">
              Use {'{{firstname}}'}, {'{{name}}'}, {'{{phone}}'} etc. — trocados pelos dados do
              contato no envio.
            </span>
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-n-200 border-t bg-n-50 px-5 py-3">
          <Button onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSave}>
            {pending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : editing ? (
              'Salvar'
            ) : (
              'Criar'
            )}
          </Button>
        </footer>
      </div>
    </div>
  );
}

interface ItemDoMenu {
  chave: string;
  icone: typeof Paperclip;
  rotulo: string;
  onClick: () => void;
  desabilitado?: boolean;
  /** Liga/desliga (assinatura, nota): mostra o ✓ quando ligado. */
  ativo?: boolean;
  /** Por que está desabilitado (ex.: travado pelo gestor). */
  dica?: string;
}

/**
 * "+" com as ações do composer (anexar, agendar, emoji, rápidas, assinatura,
 * nota) — usado com a lista de atendimentos escondida, igual ao app do
 * celular: fica DENTRO do campo, à esquerda, e vira × aberto. Abre pra cima;
 * fecha ao escolher, ao clicar fora ou no Esc. O seletor de emoji abre preso
 * ao próprio "+" (`gatilhoRef`). Na tela larga o emoji fica dentro do campo,
 * à direita.
 */
function MenuDeAcoesDoComposer({
  itens,
  aberto,
  onAbrirFechar,
  gatilhoRef,
}: {
  itens: ItemDoMenu[];
  aberto: boolean;
  onAbrirFechar: (aberto: boolean) => void;
  gatilhoRef: React.RefObject<HTMLButtonElement>;
}) {
  const caixaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent): void => {
      if (!caixaRef.current?.contains(e.target as Node)) onAbrirFechar(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onAbrirFechar(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto, onAbrirFechar]);

  return (
    <div ref={caixaRef} className="relative flex-shrink-0">
      <button
        ref={gatilhoRef}
        type="button"
        aria-label="Mais opções"
        aria-haspopup="menu"
        aria-expanded={aberto}
        title="Mais opções"
        onClick={() => onAbrirFechar(!aberto)}
        className="flex h-[32px] w-[32px] items-center justify-center rounded-pill bg-brand-50 text-brand-600 transition hover:bg-brand-100"
      >
        <Plus
          size={20}
          strokeWidth={2}
          className={`transition-transform duration-200 ${aberto ? 'rotate-45' : ''}`}
        />
      </button>
      {aberto && (
        <div
          role="menu"
          aria-label="Opções da mensagem"
          className="absolute bottom-[calc(100%+8px)] left-0 z-30 min-w-[220px] rounded-md border border-n-200 bg-n-0 py-1 shadow-md"
        >
          {itens.map((item) => {
            const Icone = item.icone;
            return (
              <button
                key={item.chave}
                type="button"
                role="menuitem"
                disabled={item.desabilitado}
                title={item.dica}
                onClick={() => {
                  onAbrirFechar(false);
                  item.onClick();
                }}
                className="flex h-[40px] w-full items-center gap-3 px-3.5 text-left font-medium text-[13px] text-n-700 transition-colors hover:bg-n-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Icone
                  size={18}
                  strokeWidth={1.75}
                  className={`flex-shrink-0 ${item.ativo ? 'text-brand-600' : 'text-n-500'}`}
                />
                <span className="flex-1">{item.rotulo}</span>
                {item.ativo && <Check size={15} strokeWidth={2.25} className="text-brand-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComposerIcon({
  icon: Icon,
  label,
  onClick,
  disabled,
  spinning,
  active,
  activeClassName,
  locked,
}: {
  icon: typeof Paperclip;
  label: string;
  onClick?: () => void;
  disabled?: boolean | undefined;
  spinning?: boolean | undefined;
  /** Quando true, ícone fica destacado em brand-500 (estado "ligado/selecionado"). */
  active?: boolean | undefined;
  /** Sobrescreve o estilo `active` (default brand). Ex: âmbar pra nota interna. */
  activeClassName?: string | undefined;
  /** Travado (ex: assinatura bloqueada pelo gestor): visível mas inerte. NÃO usa
   *  o `disabled` nativo — um botão disabled não dispara o tooltip (title) no
   *  hover no Chrome. Aqui fica clicável-no-DOM porém no-op, com aparência de
   *  desabilitado, pro tooltip aparecer. */
  locked?: boolean | undefined;
}) {
  const baseStyles = active
    ? (activeClassName ?? 'bg-brand-50 text-brand-600 hover:bg-brand-100 hover:text-brand-700')
    : 'text-n-500 hover:bg-n-100 hover:text-n-700';
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      aria-disabled={locked || undefined}
      title={label}
      onClick={locked ? undefined : onClick}
      disabled={disabled}
      className={`flex h-[36px] w-[36px] items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:opacity-40 ${locked ? 'cursor-not-allowed opacity-40' : ''} ${baseStyles}`}
    >
      <Icon size={18} strokeWidth={1.75} className={spinning ? 'animate-spin' : ''} />
    </button>
  );
}

/** Tamanho legível: "245 KB", "3,4 MB". */
function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Faixa de miniaturas dos anexos antes de enviar (decisão do dono: faixa acima
 * do campo, não tela grande). Cada miniatura mostra se está carregando ou se
 * falhou (com o motivo no tooltip) e tem o X pra remover. O "+" no fim abre o
 * seletor de novo, enquanto couber.
 */
function FaixaDeAnexos({
  anexos,
  onRemover,
  onAdicionar,
}: {
  anexos: PendingAttachment[];
  onRemover: (key: string) => void;
  /** Ausente = lista cheia (sem o "+"). */
  onAdicionar?: () => void;
}) {
  const total = anexos.reduce((soma, a) => soma + a.size, 0);
  const comErro = anexos.filter((a) => a.status === 'erro').length;
  return (
    <div className="flex items-start gap-2 border-n-100 border-b px-4 py-2">
      {/* pt/pr: espaço pro X que sai do canto (a rolagem lateral corta o que
          passa da borda). gap-3: o X de uma não encosta na seguinte. */}
      <div className="flex min-w-0 flex-1 items-start gap-3 overflow-x-auto pt-[8px] pr-[8px] pb-0.5">
        {anexos.map((a) => {
          const { Icon } = LIB_TYPE_META[a.type];
          const miniatura =
            a.type === 'image' ? a.previewUrl : a.type === 'video' ? (a.poster ?? '') : '';
          return (
            <div
              key={a.key}
              className="relative w-[56px] flex-shrink-0"
              // Sem a dica preta do navegador (pedido do dono) — o nome já
              // está embaixo. Só o arquivo com erro mantém: é onde o motivo
              // completo aparece (o texto vermelho embaixo fica cortado).
              {...(a.status === 'erro' && { title: `${a.filename} — ${a.erro ?? 'falhou'}` })}
            >
              <div
                className={`relative flex h-[56px] w-[56px] items-center justify-center overflow-hidden rounded-md border ${
                  a.status === 'erro' ? 'border-danger-500 bg-danger-50' : 'border-n-200 bg-n-50'
                }`}
              >
                {miniatura ? (
                  <img src={miniatura} alt={a.filename} className="h-full w-full object-cover" />
                ) : (
                  <Icon size={20} strokeWidth={1.75} className="text-brand-500" />
                )}
                {a.status === 'carregando' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <Loader2 size={16} className="animate-spin text-brand-500" />
                  </div>
                )}
                {a.status === 'erro' && (
                  // Cor fixa: os tokens são var(--x) em hex e não aceitam o
                  // `/opacidade` do Tailwind (bg-danger-500/10 saía transparente).
                  <div className="absolute inset-0 flex items-center justify-center bg-[rgba(239,68,68,0.14)]">
                    <AlertCircle size={18} className="text-danger-600" />
                  </div>
                )}
              </div>
              {/* X fora da miniatura (no canto, por cima da borda), no estilo
                  dos botões claros do sistema: superfície + borda cinza + sombra
                  leve — a borda/sombra é o que o separa de foto clara. Tokens
                  do tema (acompanha o escuro); vermelho só no hover. */}
              <button
                type="button"
                onClick={() => onRemover(a.key)}
                aria-label={`Remover ${a.filename}`}
                className="absolute -top-[6px] -right-[6px] z-10 flex h-[20px] w-[20px] items-center justify-center rounded-pill border border-n-200 bg-n-0 text-n-500 shadow-xs transition hover:border-danger-500 hover:text-danger-600"
              >
                <X size={11} strokeWidth={2.25} />
              </button>
              <span
                className={`mt-0.5 block truncate text-center text-[10px] ${
                  a.status === 'erro' ? 'text-danger-600' : 'text-n-500'
                }`}
              >
                {a.status === 'erro' ? (a.erro ?? 'Falhou') : a.filename}
              </span>
            </div>
          );
        })}
        {onAdicionar && (
          <button
            type="button"
            onClick={onAdicionar}
            aria-label="Adicionar mais arquivos"
            className="flex h-[56px] w-[56px] flex-shrink-0 items-center justify-center rounded-md border border-n-300 border-dashed text-n-500 transition hover:border-brand-400 hover:text-brand-500"
          >
            <Plus size={18} strokeWidth={1.75} />
          </button>
        )}
      </div>
      <div className="flex-shrink-0 pt-1 text-right text-[11px] leading-tight text-n-500">
        <div className="font-medium text-n-700">
          {anexos.length === 1 ? '1 arquivo' : `${anexos.length} arquivos`}
        </div>
        <div>{tamanhoLegivel(total)}</div>
        {comErro > 0 && (
          <div className="text-danger-600">
            {comErro === 1 ? '1 com erro' : `${comErro} com erro`}
          </div>
        )}
      </div>
    </div>
  );
}
